import { useState, useEffect, useRef, useCallback } from 'react';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  TrashIcon,
  PlusIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import type { Chapter, ChapterPart } from '@dnyaneshwari/shared';
import PartSyncPanel from './PartSyncPanel';
import ChapterEditPanel from './ChapterEditPanel';
import {
  watchParts,
  createPart,
  updatePart,
  deletePart,
  reorderParts,
  uploadPartVideo,
  uploadPartAudio,
  migrateLegacyMedia,
  reprocessPart,
  republishAudio,
  keepAudioOnly,
  discardLegacyMedia,
  upsertChapter,
} from '../services/adminService';

/**
 * One source of column widths for the parts table. The header and each row are
 * separate elements, so shared fixed widths are what keeps them aligned -- with
 * per-element grids the auto columns sized independently and drifted apart.
 */
const COL = {
  index: 'w-12 flex-shrink-0',
  title: 'flex-1 min-w-0',
  range: 'w-40 flex-shrink-0',
  length: 'w-14 flex-shrink-0 text-right',
  files: 'w-40 flex-shrink-0 min-w-0',
  action: 'w-6 flex-shrink-0',
};

function formatDuration(ms: number): string {
  if (!ms) return '—';
  const total = Math.round(ms / 1000);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

/**
 * Text input that keeps its own draft while focused and commits on blur, so a
 * Firestore round trip per keystroke never fights the cursor.
 */
function EditableField({
  value,
  onCommit,
  type = 'text',
  className = '',
  placeholder,
}: {
  value: string | number;
  onCommit: (next: string) => Promise<void> | void;
  type?: 'text' | 'number';
  className?: string;
  placeholder?: string;
}) {
  const [draft, setDraft] = useState(String(value ?? ''));
  const [saving, setSaving] = useState(false);
  const focused = useRef(false);

  useEffect(() => {
    if (!focused.current) setDraft(String(value ?? ''));
  }, [value]);

  async function commit() {
    focused.current = false;
    if (draft === String(value ?? '')) return;
    setSaving(true);
    try {
      await onCommit(draft);
    } finally {
      setSaving(false);
    }
  }

  return (
    <input
      type={type}
      value={draft}
      placeholder={placeholder}
      onFocus={() => { focused.current = true; }}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      className={`px-2 py-1.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400 ${
        saving ? 'border-amber-300 bg-amber-50' : 'border-gray-200'
      } ${className}`}
    />
  );
}

function StatusBadge({ part }: { part: ChapterPart }) {
  const map: Record<ChapterPart['status'], string> = {
    empty: 'bg-gray-100 text-gray-500',
    processing: 'bg-amber-100 text-amber-700',
    ready: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
  };
  const label: Record<ChapterPart['status'], string> = {
    empty: 'No file',
    processing: 'Processing…',
    ready: 'Ready',
    failed: 'Failed',
  };
  return (
    <span
      className={`px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${map[part.status]}`}
      title={part.errorMessage}
    >
      {label[part.status]}
    </span>
  );
}

function UploadButton({
  label,
  accept,
  colour,
  url,
  fileName,
  confirmReplace,
  onPick,
}: {
  label: string;
  accept: string;
  colour: string;
  /** Present once a file is attached: the tick opens it, to check what landed. */
  url?: string;
  fileName?: string;
  /** Shown before overwriting an existing file, when losing it would matter. */
  confirmReplace?: string;
  onPick: (file: File, onProgress: (pct: number) => void) => Promise<unknown>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [pct, setPct] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handle(file: File) {
    if (confirmReplace && url && !confirm(confirmReplace)) return;
    setError(null);
    setPct(0);
    try {
      await onPick(file, setPct);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setPct(null);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <div className="flex flex-col items-start">
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handle(f); }}
      />
      <span className="flex items-center gap-1">
        <button
          onClick={() => inputRef.current?.click()}
          disabled={pct !== null}
          className={`text-xs font-medium hover:underline disabled:opacity-50 disabled:no-underline ${colour}`}
        >
          {pct === null ? label : `${pct}%`}
        </button>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            title={fileName ? `Open ${fileName}` : 'Open uploaded file'}
            className="text-[11px] text-green-600 hover:text-green-800"
          >
            ✓
          </a>
        )}
      </span>
      {error && <span className="text-[10px] text-red-600 mt-0.5">{error}</span>}
    </div>
  );
}

/** Gaps and overlaps between part ovi ranges, and whether the chapter is covered. */
function coverageIssues(parts: ChapterPart[], totalOvis: number): string[] {
  const issues: string[] = [];
  const ranged = parts
    .filter((p) => p.oviFrom > 0 || p.oviTo > 0)
    .sort((a, b) => a.oviFrom - b.oviFrom);

  for (const p of ranged) {
    if (p.oviTo < p.oviFrom) issues.push(`${p.titleMarathi}: ovi range runs backwards`);
  }
  for (let i = 1; i < ranged.length; i++) {
    const prev = ranged[i - 1];
    const cur = ranged[i];
    if (cur.oviFrom <= prev.oviTo) {
      issues.push(`${prev.titleMarathi} and ${cur.titleMarathi} overlap at ovi ${cur.oviFrom}`);
    } else if (cur.oviFrom > prev.oviTo + 1) {
      issues.push(`Gap: ovis ${prev.oviTo + 1}–${cur.oviFrom - 1} belong to no part`);
    }
  }
  if (ranged.length && totalOvis > 0) {
    const last = ranged[ranged.length - 1];
    if (last.oviTo < totalOvis) {
      issues.push(`Ovis ${last.oviTo + 1}–${totalOvis} are not covered by any part`);
    }
  }
  if (parts.length && !ranged.length) issues.push('No part has an ovi range set yet');
  return issues;
}

function PartsPanel({
  chapterId,
  chapter,
  onChapterChanged,
}: {
  chapterId: string;
  chapter: Chapter | null;
  onChapterChanged: () => void;
}) {
  const [parts, setParts] = useState<ChapterPart[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [listenError, setListenError] = useState<string | null>(null);
  const [syncPart, setSyncPart] = useState<ChapterPart | null>(null);

  useEffect(() => {
    setLoading(true);
    setListenError(null);
    const stop = watchParts(
      chapterId,
      (next) => { setParts(next); setListenError(null); setLoading(false); },
      (message) => { setListenError(message); setLoading(false); }
    );
    return stop;
  }, [chapterId]);

  const patch = useCallback(
    (partId: string, data: Partial<ChapterPart>) => updatePart(chapterId, partId, data),
    [chapterId]
  );

  async function move(partId: string, delta: number) {
    const ids = parts.map((p) => p.id);
    const from = ids.indexOf(partId);
    const to = from + delta;
    if (to < 0 || to >= ids.length) return;
    [ids[from], ids[to]] = [ids[to], ids[from]];
    setBusy(true);
    try { await reorderParts(chapterId, ids); } finally { setBusy(false); }
  }

  async function remove(part: ChapterPart) {
    if (!confirm(`Delete ${part.titleMarathi}? The uploaded files stay in Storage.`)) return;
    setBusy(true);
    try { await deletePart(chapterId, part.id); } finally { setBusy(false); }
  }

  async function discard() {
    if (
      !confirm(
        'Delete this chapter’s existing recording and its files for good? ' +
          'The chapter will have no media until you upload parts.'
      )
    )
      return;
    setBusy(true);
    try { await discardLegacyMedia(chapterId); onChapterChanged(); } finally { setBusy(false); }
  }

  async function retry(part: ChapterPart) {
    // Audio uploads are published by their own function; a video by the
    // extraction pipeline.
    const audio = part.audioSource === 'uploaded';
    if (!confirm(`Run processing again for ${part.titleMarathi}? ${audio ? 'This re-encodes the uploaded audio and takes about a minute.' : 'This re-reads the uploaded video and takes a few minutes.'}`)) return;
    setBusy(true);
    try {
      if (audio) await republishAudio(chapterId, part.id);
      else await reprocessPart(chapterId, part.id);
    } catch (e) {
      alert(`Could not reprocess: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function audioOnly(part: ChapterPart) {
    if (!confirm(`Mark ${part.titleMarathi} ready with its audio file only? The video stays in storage but is not used.`)) return;
    setBusy(true);
    try { await keepAudioOnly(chapterId, part.id); } finally { setBusy(false); }
  }

  async function adopt() {
    setBusy(true);
    try {
      const moved = await migrateLegacyMedia(chapterId);
      if (!moved) alert('Nothing to import — this chapter has no standalone recording.');
    } finally { setBusy(false); }
  }

  const issues = coverageIssues(parts, chapter?.totalOvis ?? 0);
  const totalMs = parts.reduce((s, p) => s + (p.durationMs || 0), 0);
  const hasLegacy = Boolean(chapter?.media?.videoUrl || chapter?.media?.audioUrl);
  const released = chapter?.mediaReleased === true;
  const readyParts = parts.filter((p) => p.status === 'ready').length;

  async function setReleased(next: boolean) {
    if (
      next &&
      !confirm(
        `Release chapter ${parseInt(chapterId, 10)} to the app? Users will see ${readyParts} part${readyParts === 1 ? '' : 's'} in Listen and Video straight away.`
      )
    )
      return;
    setBusy(true);
    try {
      await upsertChapter({ id: chapterId.padStart(2, '0'), mediaReleased: next });
      onChapterChanged();
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <div className="p-4 text-sm text-gray-400">Loading parts…</div>;

  if (listenError) {
    return (
      <div className="p-4">
        <div className="flex items-start gap-2 px-3 py-2 bg-red-50 border border-red-200 rounded-lg">
          <ExclamationTriangleIcon className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-red-800">
            <p className="font-medium">Could not read this chapter&apos;s parts.</p>
            <p className="mt-0.5">{listenError}</p>
            <p className="mt-1 text-red-700">
              If this says permissions, deploy the Firestore rules:{' '}
              <code className="bg-red-100 px-1 rounded">firebase deploy --only firestore:rules</code>
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4">
      {/* Release gate. Uploads never show in the app until this is switched
          on, so content can be prepared and checked at leisure. */}
      <div
        className={`flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg border mb-3 ${
          released ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
        }`}
      >
        <div className="text-xs">
          <p className={`font-semibold ${released ? 'text-green-800' : 'text-amber-800'}`}>
            {released ? 'Released — visible in the app' : 'On hold — the app shows "coming soon"'}
          </p>
          <p className={released ? 'text-green-700' : 'text-amber-700'}>
            {released
              ? 'Users can play this chapter’s recordings. Put it on hold to hide them again.'
              : readyParts > 0
                ? `${readyParts} ready part${readyParts === 1 ? '' : 's'} waiting. Release when the content is checked.`
                : 'Upload and check the parts, then release the chapter.'}
          </p>
        </div>
        <button
          onClick={() => setReleased(!released)}
          disabled={busy || (!released && readyParts === 0 && !hasLegacy)}
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold whitespace-nowrap disabled:opacity-50 ${
            released
              ? 'border border-amber-300 bg-white text-amber-800 hover:bg-amber-50'
              : 'bg-green-600 text-white hover:bg-green-700'
          }`}
        >
          {released ? 'Put on hold' : 'Release to app'}
        </button>
      </div>

      <div className="flex items-center justify-between mb-3">
        <p className="text-xs text-gray-500">
          {parts.length === 0
            ? 'No parts yet'
            : `${parts.length} part${parts.length > 1 ? 's' : ''} · ${formatDuration(totalMs)} total`}
        </p>
        <div className="flex gap-2">
          {hasLegacy && parts.length === 0 && (
            <>
              <button
                onClick={adopt}
                disabled={busy}
                className="px-3 py-1.5 border border-gray-200 bg-white rounded-lg text-xs font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                Import existing recording
              </button>
              <button
                onClick={discard}
                disabled={busy}
                className="px-3 py-1.5 border border-red-200 text-red-700 bg-white rounded-lg text-xs font-medium hover:bg-red-50 disabled:opacity-50"
              >
                Discard recording
              </button>
            </>
          )}
          <button
            onClick={async () => { setBusy(true); try { await createPart(chapterId); } finally { setBusy(false); } }}
            disabled={busy}
            className="flex items-center gap-1 px-3 py-1.5 bg-amber-600 text-white rounded-lg text-xs font-medium hover:bg-amber-700 disabled:opacity-50"
          >
            <PlusIcon className="w-3.5 h-3.5" />
            Add part
          </button>
        </div>
      </div>

      {issues.length > 0 && (
        <div className="mb-3 flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg">
          <ExclamationTriangleIcon className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800 space-y-0.5">
            {issues.map((i) => <p key={i}>{i}</p>)}
          </div>
        </div>
      )}
      {parts.length > 0 && issues.length === 0 && (
        <div className="mb-3 flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircleIcon className="w-4 h-4 text-green-600 flex-shrink-0" />
          <p className="text-xs text-green-800">
            {/* Without an ovi count there is nothing to check coverage against,
                so claiming the chapter is covered would be a false all-clear. */}
            {(chapter?.totalOvis ?? 0) > 0
              ? 'Every ovi in this chapter is covered by a part.'
              : 'No gaps or overlaps between parts. Import this chapter’s ovis to check full coverage.'}
          </p>
        </div>
      )}

      {parts.length === 0 ? (
        <p className="text-sm text-gray-400 py-6 text-center">
          Add a part, then upload its video. The audio track is extracted automatically.
        </p>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="flex gap-3 px-3 py-2 bg-gray-50 border-b border-gray-200 text-[11px] font-semibold text-gray-500 uppercase tracking-wide">
            <span className={COL.index}>#</span>
            <span className={COL.title}>Marathi title</span>
            <span className={COL.title}>English title</span>
            <span className={COL.range}>Ovi range</span>
            <span className={COL.length}>Length</span>
            <span className={COL.files}>Files</span>
            <span className={COL.action} />
          </div>
          {parts.map((part, i) => (
            <div
              key={part.id}
              className="flex gap-3 px-3 py-2.5 border-b border-gray-100 last:border-b-0 items-center hover:bg-gray-50"
            >
              <div className={`flex items-center gap-1 ${COL.index}`}>
                <span className="w-6 h-6 rounded bg-gray-100 text-gray-600 text-xs flex items-center justify-center font-medium">
                  {part.index}
                </span>
                <div className="flex flex-col">
                  <button
                    onClick={() => move(part.id, -1)}
                    disabled={i === 0 || busy}
                    className="text-gray-300 hover:text-gray-600 disabled:opacity-30"
                    aria-label="Move up"
                  >
                    <ArrowUpIcon className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => move(part.id, 1)}
                    disabled={i === parts.length - 1 || busy}
                    className="text-gray-300 hover:text-gray-600 disabled:opacity-30"
                    aria-label="Move down"
                  >
                    <ArrowDownIcon className="w-3 h-3" />
                  </button>
                </div>
              </div>

              <EditableField
                value={part.titleMarathi}
                placeholder="भाग"
                onCommit={(v) => patch(part.id, { titleMarathi: v })}
                className={COL.title}
              />
              <EditableField
                value={part.titleEnglish}
                placeholder="Part"
                onCommit={(v) => patch(part.id, { titleEnglish: v })}
                className={COL.title}
              />

              <div className={`flex items-center gap-1 ${COL.range}`}>
                <EditableField
                  type="number"
                  placeholder="from"
                  value={part.oviFrom}
                  onCommit={(v) => patch(part.id, { oviFrom: Number(v) || 0 })}
                  className="w-16"
                />
                <span className="text-gray-400 text-xs">–</span>
                <EditableField
                  type="number"
                  placeholder="to"
                  value={part.oviTo}
                  onCommit={(v) => patch(part.id, { oviTo: Number(v) || 0 })}
                  className="w-16"
                />
              </div>

              <span className={`text-xs text-gray-500 tabular-nums whitespace-nowrap ${COL.length}`}>
                {formatDuration(part.durationMs)}
              </span>

              <div className={`flex flex-col gap-1 items-start ${COL.files}`}>
                <StatusBadge part={part} />
                <div className="flex gap-2">
                  <UploadButton
                    label="Video"
                    accept="video/*"
                    colour="text-purple-600"
                    url={part.videoUrl}
                    fileName={part.videoFileName}
                    confirmReplace={`Replace the video on ${part.titleMarathi}?`}
                    onPick={(file, onProgress) =>
                      uploadPartVideo(chapterId, part.id, file, onProgress)
                    }
                  />
                  <UploadButton
                    label="Audio"
                    accept="audio/*"
                    colour="text-blue-600"
                    url={part.audioUrl}
                    fileName={part.audioFileName}
                    confirmReplace={
                      part.audioSource === 'extracted'
                        ? `${part.titleMarathi} is using the audio extracted from its video. Replace it with your own file?`
                        : `Replace the audio on ${part.titleMarathi}?`
                    }
                    onPick={(file, onProgress) =>
                      uploadPartAudio(chapterId, part.id, file, onProgress)
                    }
                  />
                </div>
                {part.audioUrl && (
                  <span className="text-[10px] text-gray-400">
                    {part.audioSource === 'uploaded' ? 'own audio file' : 'audio from video'}
                    {part.audioKey && ` · streams ${part.audioBitrateKbps ?? 96} kbps from Cloudflare`}
                  </span>
                )}
                {part.videoFileName && (
                  <span className="block text-[10px] text-gray-400 truncate" title={part.videoFileName}>
                    🎬 {part.videoFileName}
                  </span>
                )}
                {part.audioFileName && (
                  <span className="block text-[10px] text-gray-400 truncate" title={part.audioFileName}>
                    🎵 {part.audioFileName}
                  </span>
                )}
                {(part.status === 'processing' || part.status === 'failed') && (
                  <span className="flex flex-wrap gap-x-2 text-[11px] font-medium">
                    <button
                      onClick={() => retry(part)}
                      disabled={busy}
                      className="text-amber-700 hover:underline disabled:opacity-50"
                      title={part.audioSource === 'uploaded' ? 'Encode and publish the uploaded audio again' : 'Run the audio extraction again for this video'}
                    >
                      {part.status === 'failed' ? 'Failed. Retry' : 'Still processing? Retry'}
                    </button>
                    {part.audioUrl && (
                      <button
                        onClick={() => audioOnly(part)}
                        disabled={busy}
                        className="text-green-700 hover:underline disabled:opacity-50"
                        title="Mark the part ready on its audio file; the video is not needed for playback"
                      >
                        Skip video, keep audio
                      </button>
                    )}
                  </span>
                )}
              </div>

              <button
                onClick={() => setSyncPart(part)}
                disabled={!part.videoUrl && !part.audioUrl}
                title={part.videoUrl || part.audioUrl ? 'Time each ovi against the recording' : 'Upload the audio first'}
                className={`text-xs font-medium whitespace-nowrap disabled:opacity-30 ${part.oviTimestamps?.length ? 'text-green-700' : 'text-amber-700'} hover:underline`}
              >
                {part.oviTimestamps?.length ? `Synced ${part.oviTimestamps.length}` : 'Sync ovis'}
              </button>
              <button
                onClick={() => remove(part)}
                disabled={busy}
                className={`text-gray-300 hover:text-red-600 disabled:opacity-30 ${COL.action}`}
                aria-label="Delete part"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {syncPart && (
        <PartSyncPanel
          chapterId={chapterId}
          part={parts.find((p) => p.id === syncPart.id) ?? syncPart}
          onClose={() => setSyncPart(null)}
        />
      )}

      <p className="mt-2 text-[11px] text-gray-400">
        The app plays the audio file; that is all a part needs. Video is optional and only
        used to time the ovis from its subtitles — hand it over separately rather than
        uploading it here.
      </p>
    </div>
  );
}

function ChapterTextPanel({
  chapterId,
  chapter,
  onSaved,
}: {
  chapterId: string;
  chapter: Chapter | null;
  onSaved: () => void;
}) {
  async function save(patch: Partial<Chapter>) {
    await upsertChapter({ id: chapterId.padStart(2, '0'), ...patch });
    onSaved();
  }

  const fields: { label: string; key: keyof Chapter; type?: 'number'; wide?: boolean }[] = [
    { label: 'Marathi title', key: 'titleMarathi' },
    { label: 'English title', key: 'titleEnglish' },
    { label: 'Sanskrit title', key: 'titleSanskrit' },
    { label: 'Total ovis', key: 'totalOvis', type: 'number' },
    { label: 'Total shlokas', key: 'totalShlokas', type: 'number' },
    { label: 'Cover image URL', key: 'imageUrl', wide: true },
    { label: 'Marathi description', key: 'descriptionMarathi', wide: true },
    { label: 'English description', key: 'descriptionEnglish', wide: true },
  ];

  return (
    <div className="p-4">
      <div className="grid grid-cols-2 gap-3">
        {fields.map((f) => (
          <div key={f.key} className={f.wide ? 'col-span-2' : ''}>
            <label className="block text-[11px] font-medium text-gray-500 mb-1">{f.label}</label>
            <EditableField
              type={f.type}
              value={(chapter?.[f.key] as string | number) ?? ''}
              onCommit={(v) =>
                save({ [f.key]: f.type === 'number' ? Number(v) || 0 : v } as Partial<Chapter>)
              }
              className="w-full"
            />
          </div>
        ))}
      </div>
      <p className="mt-3 text-[11px] text-gray-400">
        Changes save when you leave a field. Ovi text itself is edited through the Ovis tab.
      </p>
    </div>
  );
}

type Tab = 'parts' | 'text' | 'content';

export default function ChapterManager({
  chapterId,
  chapter,
  onChapterSaved,
}: {
  chapterId: string;
  chapter: Chapter | null;
  onChapterSaved: () => void;
}) {
  const [tab, setTab] = useState<Tab>('parts');

  const tabs: { id: Tab; label: string }[] = [
    { id: 'parts', label: 'Parts & media' },
    { id: 'text', label: 'Chapter text' },
    { id: 'content', label: 'ओव्या व भावार्थ' },
  ];

  return (
    <div>
      <div className="flex gap-1 px-4 pt-3 border-b border-gray-200 bg-white">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-3 py-2 text-xs font-medium rounded-t-lg border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? 'border-amber-500 text-amber-700 bg-amber-50'
                : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'parts' && (
        <PartsPanel chapterId={chapterId} chapter={chapter} onChapterChanged={onChapterSaved} />
      )}
      {tab === 'text' && (
        <ChapterTextPanel chapterId={chapterId} chapter={chapter} onSaved={onChapterSaved} />
      )}
      {tab === 'content' && <ChapterEditPanel chapterId={chapterId} />}
    </div>
  );
}
